import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import ToggleOffOutlinedIcon from "@mui/icons-material/ToggleOffOutlined";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import DeleteIcon from "@mui/icons-material/Delete";
import BusinessIcon from "@mui/icons-material/Business";
import AddEmployerAttribute from "../components/AddEmployerAttribute";
import { useEffect, useState } from "react";
import { Visibility, VisibilityOff } from "@mui/icons-material";

async function fetchEmployerAttributes() {
  const response = await HttpService.getAxiosClient().get(
    window.API_URL + `/employer/attributes`
  );
  return response;
}

async function fetchEmployers() {
  const response = await HttpService.getAxiosClient().get(
    window.API_URL + `/employer/all?excludeRelations=true`
  );
  return response;
}

function remapAttributeValueForDestination(
  value,
  sourceEmployerId,
  destinationEmployerId
) {
  if (typeof value !== "string") {
    return value;
  }

  const looksLikePath = value.includes("/") || value.includes("\\");
  if (!looksLikePath) {
    return value;
  }

  const sourceId = String(sourceEmployerId);
  const destinationId = String(destinationEmployerId);

  // Replace only path-segment occurrences so unrelated numeric/text values are preserved.
  const pathSegmentRegex = new RegExp(`(^|[/\\\\])${sourceId}([/\\\\]|$)`, "g");
  return value.replace(pathSegmentRegex, `$1${destinationId}$2`);
}

export default function EmployerAttributesConfig({
  employerId = null,
  employerName,
}) {
  const queryClient = useQueryClient();
  const [rows, setRows] = useState([]);
  const [previousValues, setPreviousValues] = useState({});
  const [importAttributeDlg, setImportAttributeDlg] = useState(false);
  const [sourceEmployer, setSourceEmployer] = useState(null);
  const [selectedAttributesToImport, setSelectedAttributesToImport] = useState(
    []
  );
  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  const updateEmployeeAttributeMutation = useMutation(
    (values) => {
      return HttpService.getAxiosClient().put(
        window.API_URL + `/employer/attributes/${values.id}`,
        values.row
      );
    },
    {
      onSuccess: (data, value) => {
        setPreviousValues((prevPreviousValues) => {
          const newPreviousValues = { ...prevPreviousValues };
          delete newPreviousValues[value.id];
          return newPreviousValues;
        });
      },
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message: "Error updating the attributes",
        });
      },
    }
  );

  const deleteEmployeeAttributeMutation = useMutation(
    (values) => {
      return HttpService.getAxiosClient().delete(
        window.API_URL + `/employer/attributes/${values.id}`
      );
    },
    {
      onSuccess: (dataResponse, value) => {
        data.data = data.data.filter((row) => row.id !== value.id);
      },
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message: "Error deleting the attribute",
        });
      },
    }
  );

  const handleEditCellChange = (params, id, field, oldValue, newValue) => {
    const encryptedProperty =
      params.row.properties !== null &&
      Array.isArray(params.row.properties) &&
      params.row.properties.some(
        (property) => property.property === "ENCRYPTED"
      );
    if (!encryptedProperty) {
      if (newValue !== undefined && oldValue !== newValue) {
        setPreviousValues((prevPreviousValues) => ({
          ...prevPreviousValues,
          [id]: { ...prevPreviousValues[id], [field]: oldValue },
        }));
      }
    }
  };

  const handleCellEditCommit = (params) => {
    const currentRow = rows.find((row) => row.id === params.id);
    if (!currentRow || isEncrypted({ row: currentRow })) {
      return;
    }

    const oldValue = currentRow[params.field];
    const newValue = params.value;

    if (newValue === undefined || oldValue === newValue) {
      return;
    }

    handleEditCellChange(
      { row: currentRow },
      params.id,
      params.field,
      oldValue,
      newValue
    );

    setRows((currentRows) =>
      currentRows.map((row) =>
        row.id === params.id ? { ...row, [params.field]: newValue } : row
      )
    );
  };

  const clearEditedRow = (params) => {
    const previousRowValues = previousValues[params.id];
    if (previousRowValues) {
      setRows((currentRows) =>
        currentRows.map((row) =>
          row.id === params.id ? { ...row, ...previousRowValues } : row
        )
      );
    }
    setPreviousValues((prevPreviousValues) => {
      const newPreviousValues = { ...prevPreviousValues };
      delete newPreviousValues[params.id];
      return newPreviousValues;
    });
  };

  const isEncrypted = (params) => {
    return (
      params.row.properties !== null &&
      Array.isArray(params.row.properties) &&
      params.row.properties.some(
        (property) => property.property === "ENCRYPTED"
      )
    );
  };

  const columns = [
    { field: "id", headerName: "id", width: 10 },
    { field: "employerName", headerName: "Employer", width: 350, hide: false },
    { field: "attributeName", headerName: "Attribute", width: 350 },
    {
      field: "value",
      headerName: "Value",
      width: 350,
      editable: (params) => !isEncrypted(params),
      renderCell: (params) => {
        if (params.row.isVisible === undefined) {
          params.row.isVisible = false;
        }

        const handleToggleVisibility = () => {
          params.row.isVisible = !params.row.isVisible;
          params.api.forceUpdate(); // Use forceUpdate to refresh the cell
        };
        if (isEncrypted(params)) {
          return (
            <div style={{ display: "flex", alignItems: "center" }}>
              {params.row.isVisible ? (
                <span>{params.row.value}</span>
              ) : (
                <Visibility fontSize="16" onClick={handleToggleVisibility} />
              )}
              {params.row.isVisible && (
                <VisibilityOff
                  sx={{ marginLeft: "0.5rem" }}
                  fontSize="16"
                  onClick={handleToggleVisibility}
                />
              )}
            </div>
          );
        } else {
          return params.row.value;
        }
      },
    },
    {
      field: "lastChangedDate",
      headerName: "Last changed",
      width: 150,
      hide: true,
    },
    { field: "changedBy", headerName: "Changed by", width: 120, hide: true },
    {
      field: "action",
      headerName: "Action",
      width: 150,
      renderCell: (params) => {
        return (
          <div
            key={"action." + params.id}
            style={{ display: isEncrypted(params) ? "none" : "flex" }}
          >
            <IconButton
              disabled={!(params.id in previousValues) || isEncrypted(params)}
              key={"save." + params.id}
              onClick={() => {
                updateEmployeeAttributeMutation.mutate({ ...params });
              }}
            >
              <SaveIcon />
            </IconButton>
            {params.id in previousValues ? (
              <IconButton
                key={"clear." + params.id}
                onClick={() => {
                  clearEditedRow(params);
                }}
              >
                <CloseIcon />
              </IconButton>
            ) : (
              <>
                <IconButton
                  disabled={params.id in previousValues}
                  key={"toggle." + params.id}
                  onClick={() => {
                    const newStatus =
                      params.row.status !== "ACTIVE" ? "ACTIVE" : "INACTIVE";
                    params.row.status = newStatus;
                    updateEmployeeAttributeMutation.mutate({ ...params });
                  }}
                >
                  {params.row.status !== "ACTIVE" ? (
                    <ToggleOffOutlinedIcon />
                  ) : (
                    <ToggleOnIcon />
                  )}
                </IconButton>
                <IconButton
                  disabled={params.id in previousValues}
                  key={"delete." + params.id}
                  onClick={() => {
                    deleteEmployeeAttributeMutation.mutate({ ...params });
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </>
            )}
          </div>
        );
      },
    },
  ];

  const { data, isLoading } = useQuery(
    ["employerAttributes"],
    fetchEmployerAttributes
  );

  const filteredRows = employerId
    ? (data?.data || []).filter(
        (row) => String(row.idEmployer) === String(employerId)
      )
    : data?.data || [];

  useEffect(() => {
    const nextRows = employerId
      ? (data?.data || []).filter(
          (row) => String(row.idEmployer) === String(employerId)
        )
      : data?.data || [];
    setRows(nextRows);
  }, [data?.data, employerId]);

  const processRowUpdate = (newRow, oldRow) => {
    setRows((currentRows) =>
      currentRows.map((row) => (row.id === newRow.id ? newRow : row))
    );

    return newRow;
  };

  const { data: employersData } = useQuery(["allEmployers"], fetchEmployers, {
    enabled: importAttributeDlg,
  });

  const destinationAttributeNames = new Set(
    filteredRows.map((row) => row.attributeName)
  );

  const sourceEmployerAttributes = sourceEmployer
    ? (data?.data || []).filter(
        (row) => String(row.idEmployer) === String(sourceEmployer.idEmployer)
      )
    : [];

  const availableImportAttributes = sourceEmployerAttributes.filter(
    (row) => !destinationAttributeNames.has(row.attributeName)
  );

  const sourceEmployers = (employersData?.data || []).filter(
    (employer) => String(employer.idEmployer) !== String(employerId)
  );

  const [addAttributeDlg, setAddAttributeDlg] = useState(false);
  const importAttributesMutation = useMutation(
    async ({ destinationEmployerId, sourceEmployerId, attributes }) => {
      for (const attributeRow of attributes) {
        const payload = {
          companieList: [destinationEmployerId],
          attribute: attributeRow.attributeName,
          value: remapAttributeValueForDestination(
            attributeRow.value,
            sourceEmployerId,
            destinationEmployerId
          ),
        };

        await HttpService.getAxiosClient().post(
          window.API_URL + `/employer/attributes`,
          payload
        );
      }
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(["employerAttributes"]);
        setFeedback({
          open: true,
          type: "success",
          message: `${variables.attributes.length} attributes imported successfully.`,
        });
        setImportAttributeDlg(false);
        setSourceEmployer(null);
        setSelectedAttributesToImport([]);
      },
      onError: () => {
        setFeedback({
          open: true,
          type: "error",
          message: "Error importing attributes.",
        });
      },
    }
  );

  const onImportAttributes = () => {
    if (
      !employerId ||
      !sourceEmployer ||
      selectedAttributesToImport.length === 0
    ) {
      return;
    }

    importAttributesMutation.mutate({
      destinationEmployerId: employerId,
      sourceEmployerId: sourceEmployer.idEmployer,
      attributes: selectedAttributesToImport,
    });
  };

  const addAttributelgFunct = (event) => {
    return (
      <Dialog open={addAttributeDlg} fullWidth onClose={() => {}}>
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <BusinessIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              Add Company attributes
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setAddAttributeDlg(false);
            }}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3 }}>
          <AddEmployerAttribute
            handleClose={() => setAddAttributeDlg(false)}
            employerId={employerId}
            employerName={employerName}
          />
        </DialogContent>
      </Dialog>
    );
  };

  const importAttributelgFunct = () => {
    return (
      <Dialog
        open={importAttributeDlg}
        fullWidth
        maxWidth="md"
        onClose={() => {
          setImportAttributeDlg(false);
        }}
      >
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <BusinessIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              Import Company Attributes
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setImportAttributeDlg(false);
            }}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Destination employer"
                value={employerName || employerId || ""}
                disabled
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                options={sourceEmployers}
                value={sourceEmployer}
                onChange={(_, newValue) => {
                  setSourceEmployer(newValue);
                  setSelectedAttributesToImport([]);
                }}
                getOptionLabel={(option) =>
                  `${option.description || option.name || "Employer"} [${option.idEmployer}]`
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Source employer"
                    placeholder="Search source employer"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                multiple
                disableCloseOnSelect
                disabled={!sourceEmployer}
                options={availableImportAttributes}
                value={selectedAttributesToImport}
                onChange={(_, newValues) => {
                  setSelectedAttributesToImport(newValues);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => option.attributeName}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    <Box>
                      <Typography variant="body2">
                        {option.attributeName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Default value: {String(option.value || "")}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Attributes to import"
                    placeholder={
                      sourceEmployer
                        ? "Select missing attributes"
                        : "Select source employer first"
                    }
                    helperText={
                      sourceEmployer
                        ? `${availableImportAttributes.length} attributes available to import (missing in destination).`
                        : ""
                    }
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                onClick={onImportAttributes}
                disabled={
                  !sourceEmployer ||
                  selectedAttributesToImport.length === 0 ||
                  importAttributesMutation.isLoading
                }
              >
                Import Selected Attributes
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      {isLoading ? (
        ""
      ) : (
        <Grid container p={0} m={0} spacing={0}>
          <Grid item xs={12}>
            <Box display="flex">
              <Typography variant="h5">
                {employerId
                  ? `Company Attributes - ${employerName || employerId}`
                  : "Company Attributes"}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setAddAttributeDlg(true)}
              >
                Add Attribute
              </Button>
              {employerId && (
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ marginLeft: "0.5rem" }}
                  onClick={() => setImportAttributeDlg(true)}
                >
                  Import Attributes
                </Button>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sx={{ width: "100%", height: "80vh" }}>
            <DataGrid
              key={`${employerId || "all"}-${filteredRows.length}`}
              rows={rows}
              columns={columns}
              getRowHeight={() => 25}
              density="compact"
              onCellEditCommit={handleCellEditCommit}
              processRowUpdate={processRowUpdate}
              disableDensitySelector
              components={{ Toolbar: GridToolbar }}
              componentsProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
              isCellEditable={(params) => !isEncrypted(params)}
              getRowId={(row) => row.id}
            />
          </Grid>
        </Grid>
      )}
      {addAttributeDlg ? addAttributelgFunct() : ""}
      {importAttributeDlg ? importAttributelgFunct() : ""}
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={() => setFeedback({ ...feedback, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setFeedback({ ...feedback, open: false })}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
